import { BarChart3, MousePointerClick, ExternalLink } from "lucide-react"
import { useEffect, useState } from "react"
import { getStorefrontStats } from "../../api/storefront/get"

type StorefrontStatsCardProps = {
  store: any
  className?: string
}

type StorefrontStats = {
  totalVisits: number
  shortLinkVisits: number
  lastVisitedAt: string | null
}

const defaultStats: StorefrontStats = {
  totalVisits: 0,
  shortLinkVisits: 0,
  lastVisitedAt: null,
}

export default function StorefrontStatsCard({
  store,
  className = "",
}: StorefrontStatsCardProps) {
  const [stats, setStats] = useState<StorefrontStats>(defaultStats)
  const [loading, setLoading] = useState(false)
  const [available, setAvailable] = useState(true)

  useEffect(() => {
    if (!store) return

    let cancelled = false

    const loadStats = async () => {
      setLoading(true)
      setAvailable(true)

      try {
        const data = await getStorefrontStats(store)
        if (!data) {
          if (!cancelled) {
            setAvailable(false)
          }
          return
        }

        if (!cancelled) {
          setStats({
            totalVisits: Number(data?.totalVisits || 0),
            shortLinkVisits: Number(data?.shortLinkVisits || 0),
            lastVisitedAt: data?.lastVisitedAt || null,
          })
        }
      } catch {
        if (!cancelled) {
          setAvailable(false)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadStats()

    return () => {
      cancelled = true
    }
  }, [store])

  const lastVisitedLabel = stats.lastVisitedAt
    ? new Date(stats.lastVisitedAt).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Aucune visite"

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
          <BarChart3 size={20} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-800">Performance vitrine</p>
          <p className="mt-1 text-xs text-slate-500">
            Visites suivies par le lien direct et le lien court branded.
          </p>
        </div>
      </div>

      {!available ? (
        <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Statistiques indisponibles pour le moment. Vérifiez que `gestockweb` tourne bien sur `localhost:3000` en local, ou que l’API storefront est déployée.
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatPill
          icon={<ExternalLink size={15} />}
          label="Visites totales"
          value={loading ? "..." : String(stats.totalVisits)}
        />
        <StatPill
          icon={<MousePointerClick size={15} />}
          label="Lien court"
          value={loading ? "..." : String(stats.shortLinkVisits)}
        />
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">Dernière visite</p>
        <p className="mt-1 text-sm font-medium text-slate-700">{loading ? "Chargement..." : lastVisitedLabel}</p>
      </div>
    </div>
  )
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-slate-400">
        {icon}
        {label}
      </div>
      <p className="mt-2 text-xl font-semibold text-slate-900">{value}</p>
    </div>
  )
}
